import React, { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Platform } from 'react-native';
import {
    Extrapolation,
    SharedValue,
    interpolate,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';

import BottomSheet from '@gorhom/bottom-sheet';

import { useLocalFlagsStore } from '../devsettings/hooks/use-dev-settings';

type FantasyFullScreenTransitionContextValue = {
    animatedPosition: SharedValue<number>;
    pageAnim: SharedValue<number>;
    selectedTabSharedValue: SharedValue<number>;
    animatedIndex: SharedValue<number>;
    sheetMountAnimationProgress: SharedValue<number>;
    isSheetMountCompleted: SharedValue<boolean>;
    snapPointIndex: number;
    setSnapPointIndex: (index: number) => void;
    mountingFadeIn: ReturnType<typeof useAnimatedStyle>;
    bottomSheetCircularPagerRef: React.MutableRefObject<BottomSheet | null>;
};

const FantasyFullScreenPagerTransitionContext = React.createContext<
    FantasyFullScreenTransitionContextValue | undefined
>(undefined);

export const MOUNT_FADE_IN_DURATION = 800;

const ANIMATION_ENABLED = Platform.OS === 'ios';

export type FantasyFullScreenPagerTransitionProviderProps = {
    initialIndex?: number;
};

export const FantasyFullScreenPagerTransitionProvider: React.FC<
    PropsWithChildren<FantasyFullScreenPagerTransitionProviderProps>
> = ({ children, initialIndex }) => {
    const { localFlags } = useLocalFlagsStore();
    const slowAnimations = localFlags?.useSlowAnimations?.enabled;
    const animatedPosition = useSharedValue(0);
    const pageAnim = useSharedValue(initialIndex ?? 0);
    const animatedIndex = useSharedValue(0);
    const sheetMountAnimationProgress = useSharedValue(-1);
    const selectedTabSharedValue = useSharedValue(-1);

    const [snapPointIndex, setSnapPointIndex] = useState(0);

    const isSheetMountCompleted = useSharedValue(false);

    const bottomSheetCircularPagerRef = useRef<BottomSheet>(null);

    const mountDuration = slowAnimations ? MOUNT_FADE_IN_DURATION * 4 : MOUNT_FADE_IN_DURATION;

    useEffect(() => {
        if (!ANIMATION_ENABLED) {
            sheetMountAnimationProgress.value = 0;
            isSheetMountCompleted.value = true;
        } else {
            sheetMountAnimationProgress.value = withTiming(0, { duration: mountDuration }, () => {
                isSheetMountCompleted.value = true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const mountingFadeIn = useAnimatedStyle(() => {
        if (!isSheetMountCompleted.value) {
            return {
                opacity: interpolate(sheetMountAnimationProgress.value, [-1, 0], [0, 1], Extrapolation.CLAMP),
            };
        }

        return {
            opacity: interpolate(animatedIndex.value, [-1, 0], [0, 1], Extrapolation.CLAMP),
        };
    });

    const value: FantasyFullScreenTransitionContextValue = useMemo(() => {
        return {
            animatedPosition,
            animatedIndex,
            snapPointIndex,
            setSnapPointIndex,
            sheetMountAnimationProgress,
            mountingFadeIn,
            isSheetMountCompleted,
            selectedTabSharedValue,
            pageAnim,
            bottomSheetCircularPagerRef,
        };
    }, [
        animatedPosition,
        animatedIndex,
        snapPointIndex,
        sheetMountAnimationProgress,
        mountingFadeIn,
        isSheetMountCompleted,
        selectedTabSharedValue,
        pageAnim,
    ]);

    return (
        <FantasyFullScreenPagerTransitionContext.Provider value={value}>
            {children}
        </FantasyFullScreenPagerTransitionContext.Provider>
    );
};

export const useFantasyFullScreenPagerTransition = () => {
    const context = useContext(FantasyFullScreenPagerTransitionContext);
    if (!context) {
        throw new Error(
            'useFantasyFullScreenPagerTransition must be used within a FantasyFullScreenPagerTransitionProvider'
        );
    }
    return context;
};
